import type { ChatMessage } from "../types/coach";

export interface MessageGroup {
  role: "user" | "assistant" | "tool-use" | "tool-result";
  messages: ChatMessage[];
  showAvatar: boolean;
  showTimestamp: boolean;
}

export function groupMessages(messages: ChatMessage[]): MessageGroup[] {
  if (messages.length === 0) {
    return [];
  }

  const groups: MessageGroup[] = [];
  let currentGroup: ChatMessage[] = [];
  let currentRole: "user" | "assistant" | "tool-use" | "tool-result" | null =
    null;

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];

    // Determine the message role for grouping
    let messageRole: "user" | "assistant" | "tool-use" | "tool-result";
    if (message.internal) {
      messageRole = message.role as "tool-use" | "tool-result";
    } else {
      messageRole = message.role === "user" ? "user" : "assistant";
    }

    // Check if we need to start a new group
    const shouldStartNewGroup =
      currentRole !== messageRole ||
      currentGroup.length === 0 ||
      shouldBreakOnTime(currentGroup.at(-1)!, message);

    if (shouldStartNewGroup) {
      // Save previous group if it exists
      if (currentGroup.length > 0 && currentRole) {
        groups.push(
          createMessageGroup(currentGroup, currentRole, groups.length === 0)
        );
      }

      // Start new group
      currentGroup = [message];
      currentRole = messageRole;
    } else {
      // Add to current group
      currentGroup.push(message);
    }
  }

  // Don't forget the last group
  if (currentGroup.length > 0 && currentRole) {
    groups.push(
      createMessageGroup(currentGroup, currentRole, groups.length === 0)
    );
  }

  return groups;
}

function createMessageGroup(
  messages: ChatMessage[],
  role: "user" | "assistant" | "tool-use" | "tool-result",
  isFirst: boolean
): MessageGroup {
  return {
    role,
    messages,
    showAvatar: true, // Always show avatar for visual consistency
    showTimestamp: shouldShowTimestamp(messages, isFirst),
  };
}

function shouldBreakOnTime(
  prevMessage: ChatMessage,
  currentMessage: ChatMessage
): boolean {
  // Ensure timestamps are Date objects
  const prevTime =
    prevMessage.timestamp instanceof Date
      ? prevMessage.timestamp
      : new Date(prevMessage.timestamp);
  const currTime =
    currentMessage.timestamp instanceof Date
      ? currentMessage.timestamp
      : new Date(currentMessage.timestamp);

  const timeDiff = currTime.getTime() - prevTime.getTime();
  const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
  return timeDiff > fiveMinutes;
}

function shouldShowTimestamp(
  messages: ChatMessage[],
  isFirst: boolean
): boolean {
  // Always show timestamp for the first group
  if (isFirst) {
    return true;
  }

  // Show timestamp if the last message is more than 10 minutes old
  const lastMessage = messages.at(-1)!;
  const timestamp =
    lastMessage.timestamp instanceof Date
      ? lastMessage.timestamp
      : new Date(lastMessage.timestamp);
  const tenMinutesAgo = Date.now() - 10 * 60_000;

  return timestamp.getTime() < tenMinutesAgo;
}
